import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

function Phase10Scorecard() {
  const phaseDescriptions = [
    '2 sets of 3',
    '1 set of 3 + 1 run of 4',
    '1 set of 4 + 1 run of 4',
    '1 run of 7',
    '1 run of 8',
    '1 run of 9',
    '2 sets of 4',
    '7 cards of one color',
    '1 set of 5 + 1 set of 2',
    '1 set of 5 + 1 set of 3'
  ];

  const MAX_PLAYERS = 8;

  // Pre-loaded game data
  const initialPlayers = ['Jayden', 'Mark', 'Allison'];
  const initialScores = [
    ['10', '55', '15', '5', '50', '0', '0', '65', '5', ''],
    ['0', '0', '65', '50', '5', '100', '10', '0', '65', ''],
    ['10', '10', '0', '0', '0', '55', '105', '85', '0', '']
  ];

  const [playerCount, setPlayerCount] = useState(3);
  const [players, setPlayers] = useState(initialPlayers);
  const [rounds, setRounds] = useState(10);
  const [scores, setScores] = useState(initialScores);
  const [phaseOverrides, setPhaseOverrides] = useState({});
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('');
  const [snapshotSent, setSnapshotSent] = useState(false);
  const [winner, setWinner] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await window.storage.get('phase10-game-v3');
        if (data?.value) {
          const parsed = JSON.parse(data.value);
          if (parsed.players) setPlayers(parsed.players);
          if (parsed.scores) setScores(parsed.scores);
          if (parsed.rounds) setRounds(parsed.rounds);
          if (parsed.phaseOverrides) setPhaseOverrides(parsed.phaseOverrides);
          if (parsed.playerCount) setPlayerCount(parsed.playerCount);
        } else {
          await window.storage.set('phase10-game-v3', JSON.stringify({
            players: initialPlayers,
            scores: initialScores,
            rounds: 10,
            phaseOverrides: {},
            playerCount: 3
          }));
        }
      } catch (e) {
        console.log('Storage unavailable, using pre-loaded data');
      }
      setLoading(false);
    };
    load();
  }, []);

  const saveData = async (newPlayers, newScores, newRounds, newOverrides, newPlayerCount) => {
    try {
      await window.storage.set('phase10-game-v3', JSON.stringify({
        players: newPlayers,
        scores: newScores,
        rounds: newRounds,
        phaseOverrides: newOverrides,
        playerCount: newPlayerCount
      }));
      setSaveStatus('✓ Saved');
      setTimeout(() => setSaveStatus(''), 1500);
    } catch (e) {
      console.log('Storage unavailable');
    }
  };

  const changePlayerCount = (newCount) => {
    const count = parseInt(newCount);
    if (count < 2 || count > MAX_PLAYERS) return;

    let newPlayers = [...players];
    let newScores = [...scores];

    if (count > playerCount) {
      for (let i = playerCount; i < count; i++) {
        newPlayers.push(`Player ${i + 1}`);
        newScores.push(Array(rounds).fill(''));
      }
    } else {
      newPlayers = newPlayers.slice(0, count);
      newScores = newScores.slice(0, count);
    }

    setPlayerCount(count);
    setPlayers(newPlayers);
    setScores(newScores);
    saveData(newPlayers, newScores, rounds, phaseOverrides, count);
  };

  const calculatePhase = (playerIndex) => {
    if (phaseOverrides[playerIndex] !== undefined) {
      return phaseOverrides[playerIndex];
    }

    let phase = 1;
    for (let r = 0; r < scores[playerIndex].length; r++) {
      const score = parseInt(scores[playerIndex][r]);
      if (!isNaN(score) && score < 50) {
        phase = Math.min(10, phase + 1);
      }
    }
    return phase;
  };

  const getCurrentPhases = () => {
    return players.slice(0, playerCount).map((_, i) => calculatePhase(i));
  };

  useEffect(() => {
    let foundWinner = null;

    players.slice(0, playerCount).forEach((_, pi) => {
      let completedPhases = 0;
      for (const s of scores[pi]) {
        const score = parseInt(s);
        if (!isNaN(score) && score < 50) completedPhases++;
      }

      if (completedPhases >= 10) {
        foundWinner = pi;
      }
    });

    setWinner(foundWinner);
  }, [scores, phaseOverrides, playerCount, players]);

  const updateScore = (playerIndex, roundIndex, value) => {
    const newScores = scores.map((s, i) => i === playerIndex ? [...s] : s);
    newScores[playerIndex][roundIndex] = value;
    setScores(newScores);
    const newOverrides = { ...phaseOverrides };
    delete newOverrides[playerIndex];
    setPhaseOverrides(newOverrides);
    saveData(players, newScores, rounds, newOverrides, playerCount);
  };

  const setManualPhase = (playerIndex, phase) => {
    const newOverrides = { ...phaseOverrides, [playerIndex]: phase };
    setPhaseOverrides(newOverrides);
    saveData(players, scores, rounds, newOverrides, playerCount);
  };

  const clearOverride = (playerIndex) => {
    const newOverrides = { ...phaseOverrides };
    delete newOverrides[playerIndex];
    setPhaseOverrides(newOverrides);
    saveData(players, scores, rounds, newOverrides, playerCount);
  };

  const updatePlayer = (index, name) => {
    const newPlayers = [...players];
    newPlayers[index] = name || `Player ${index + 1}`;
    setPlayers(newPlayers);
    setEditingPlayer(null);
    saveData(newPlayers, scores, rounds, phaseOverrides, playerCount);
  };

  const addRound = () => {
    const newRounds = rounds + 1;
    const newScores = scores.map(s => [...s, '']);
    setRounds(newRounds);
    setScores(newScores);
    saveData(players, newScores, newRounds, phaseOverrides, playerCount);
  };

  const removeRound = () => {
    if (rounds <= 10) return;
    const newRounds = rounds - 1;
    const newScores = scores.map(s => s.slice(0, -1));
    setRounds(newRounds);
    setScores(newScores);
    saveData(players, newScores, newRounds, phaseOverrides, playerCount);
  };

  const getTotal = (playerIndex) => {
    return scores[playerIndex].reduce((sum, s) => sum + (parseInt(s) || 0), 0);
  };

  const currentPhases = getCurrentPhases();
  const currentRound = Math.max(0, scores[0].findIndex(s => s === ''));

  const currentDealer = currentRound % playerCount;

  const leaderboard = players.slice(0, playerCount).map((p, i) => ({
    name: p,
    index: i,
    total: getTotal(i),
    phase: currentPhases[i]
  })).filter(p => p.total > 0 || p.phase > 1).sort((a, b) => {
    if (b.phase !== a.phase) return b.phase - a.phase;
    return a.total - b.total;
  });

  const getMedal = (rank) => ['🥇', '🥈', '🥉'][rank] || `${rank + 1}.`;

  const shareSnapshot = async () => {
    let text = '🎯 PHASE 10 🎯\n';
    text += `Round: ${currentRound + 1} of ${rounds}\n`;
    text += `🃏 ${players[currentDealer]} shuffling\n\n`;

    if (winner !== null) {
      text += `🏆 ${players[winner]} WINS! 🏆\n\n`;
    }

    text += '🏆 LEADERBOARD:\n';
    leaderboard.forEach((p, i) => {
      text += `${getMedal(i)} ${p.name}: Phase ${p.phase} (${p.total} pts)\n`;
    });
    text += '\n📊 ALL SCORES:\n';
    players.slice(0, playerCount).forEach((p, i) => {
      text += `${p}: Phase ${currentPhases[i]} - ${getTotal(i)} pts\n`;
    });

    try {
      if (navigator.share) {
        await navigator.share({ text });
      } else {
        await navigator.clipboard.writeText(text);
        setSnapshotSent(true);
        setTimeout(() => setSnapshotSent(false), 2000);
      }
    } catch (e) {
      await navigator.clipboard.writeText(text);
      setSnapshotSent(true);
      setTimeout(() => setSnapshotSent(false), 2000);
    }
  };

  const resetGame = () => {
    if (window.confirm('Reset all scores and phases?')) {
      const emptyScores = Array(playerCount).fill(null).map(() => Array(10).fill(''));
      setScores(emptyScores);
      setRounds(10);
      setPhaseOverrides({});
      setWinner(null);
      saveData(players, emptyScores, 10, {}, playerCount);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-cyan-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-cyan-900 p-3 text-white">
      <div className="max-w-2xl mx-auto">
        {/* Back button */}
        <Link to="/" className="inline-block mb-2 text-blue-300 hover:text-white text-sm">
          ← Back to Games
        </Link>

        {/* Header */}
        <div className="text-center mb-4">
          <h1 className="text-3xl font-bold text-cyan-300">🎯 Phase 10</h1>
          <p className="text-blue-300 text-sm">
            {saveStatus || `Round ${currentRound + 1} of ${rounds}`}
          </p>
          <p className="text-amber-300 text-sm mt-1">
            🃏 {players[currentDealer]} shuffling
          </p>
        </div>

        {/* Player Count Selector */}
        <div className="flex justify-center items-center gap-3 mb-4">
          <span className="text-blue-300 text-sm">Players:</span>
          <div className="flex gap-1">
            {[2, 3, 4, 5, 6, 7, 8].map(num => (
              <button
                key={num}
                onClick={() => changePlayerCount(num)}
                className={`w-8 h-8 rounded-lg font-bold text-sm transition-all ${
                  playerCount === num
                    ? 'bg-cyan-500 text-blue-950'
                    : 'bg-blue-800 text-blue-300 hover:bg-blue-700'
                }`}
              >
                {num}
              </button>
            ))}
          </div>
        </div>

        {/* Winner Banner */}
        {winner !== null && (
          <div className="bg-gradient-to-r from-yellow-500 via-amber-400 to-yellow-500 rounded-xl p-4 mb-4 text-center animate-pulse">
            <div className="text-3xl mb-1">🏆🎉🏆</div>
            <div className="text-blue-950 font-bold text-xl">{players[winner]} WINS!</div>
            <div className="text-blue-900 text-sm">Completed all 10 phases with {getTotal(winner)} points</div>
          </div>
        )}

        {/* Leaderboard */}
        {leaderboard.length > 0 && (
          <div className="bg-blue-800/50 rounded-xl p-4 mb-4 backdrop-blur border border-blue-600/30">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-cyan-300 font-bold">🏆 LEADERBOARD</h2>
              <div className="flex gap-2">
                <button
                  onClick={shareSnapshot}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    snapshotSent ? 'bg-green-500 text-white' : 'bg-cyan-500 text-blue-950 hover:bg-cyan-400'
                  }`}
                >
                  {snapshotSent ? '✓ Copied!' : '📤 Share'}
                </button>
                <button
                  onClick={() => window.open('/phase-10/tv', '_blank')}
                  className="px-4 py-1.5 rounded-lg text-sm font-medium bg-purple-600 hover:bg-purple-500 text-white transition-all"
                >
                  📺 TV
                </button>
              </div>
            </div>
            <div className="grid gap-2">
              {leaderboard.map((p, rank) => (
                <div key={p.index} className={`flex items-center justify-between px-3 py-2 rounded-lg ${
                  rank === 0 ? 'bg-cyan-500/20 text-cyan-200 border border-cyan-400/30' : 'text-blue-200 bg-blue-900/30'
                }`}>
                  <span className="font-medium">
                    {winner === p.index ? '🏆 ' : ''}{getMedal(rank)} {p.name}
                  </span>
                  <span className="text-sm font-mono">
                    Phase {p.phase} • {p.total} pts
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Phase Selection Row */}
        <div className="bg-blue-800/30 rounded-xl p-3 mb-3 border border-blue-700/30">
          <p className="text-xs text-blue-300 mb-2 text-center">
            Auto-calculated (score &lt;50 = phase complete) • Tap to override
          </p>
          <div className="flex justify-center gap-2 flex-wrap">
            {players.slice(0, playerCount).map((player, pi) => (
              <div key={pi} className={`flex flex-col items-center rounded-lg p-2 min-w-[70px] ${
                currentDealer === pi ? 'bg-amber-500/20 border border-amber-400/50' : 'bg-blue-900/50'
              }`}>
                <span className="text-xs text-blue-200 font-medium truncate max-w-[60px]">
                  {currentDealer === pi ? '🃏 ' : ''}{player.slice(0, 6)}
                </span>
                <select
                  value={currentPhases[pi]}
                  onChange={(e) => setManualPhase(pi, parseInt(e.target.value))}
                  className="mt-1 bg-blue-700 text-cyan-300 text-base rounded-lg px-2 py-1 font-bold cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-400"
                >
                  {[1,2,3,4,5,6,7,8,9,10].map(p => (
                    <option key={p} value={p}>P{p}</option>
                  ))}
                </select>
                {phaseOverrides[pi] !== undefined && (
                  <button
                    onClick={() => clearOverride(pi)}
                    className="text-xs text-blue-400 hover:text-cyan-300 mt-1"
                  >
                    (auto)
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Score Table */}
        <div className="overflow-x-auto rounded-xl border border-blue-700/50">
          <table className="text-sm border-collapse bg-blue-900/60 backdrop-blur">
            <thead>
              <tr className="bg-blue-800">
                <th className="p-2 text-cyan-300 text-sm font-bold sticky left-0 bg-blue-800 z-20 w-12">Rnd</th>
                {players.slice(0, playerCount).map((player, i) => (
                  <th key={i} className={`p-2 min-w-[80px] max-w-[120px] transition-all ${
                    currentDealer === i ? 'bg-amber-500/30' : ''
                  }`}>
                    {editingPlayer === i ? (
                      <input
                        autoFocus
                        defaultValue={player}
                        onBlur={(e) => updatePlayer(i, e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && updatePlayer(i, e.target.value)}
                        className="w-full bg-blue-700 text-white text-center rounded px-2 py-1 text-sm"
                      />
                    ) : (
                      <button
                        onClick={() => setEditingPlayer(i)}
                        className={`w-full text-sm py-1 px-1 rounded transition-colors ${
                          currentDealer === i ? 'text-amber-300 font-bold' :
                          winner === i ? 'text-yellow-300 bg-yellow-500/20' :
                          leaderboard[0]?.index === i ? 'text-cyan-300 bg-cyan-500/20' : 'text-white hover:bg-blue-700'
                        }`}
                      >
                        {currentDealer === i ? '🃏 ' : ''}{winner === i ? '🏆 ' : ''}{player.slice(0, 7)}
                      </button>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: rounds }, (_, ri) => {
                const roundDealer = ri % playerCount;
                return (
                  <tr key={ri} className={`border-t border-blue-700/30 ${ri === currentRound ? 'bg-blue-700/30' : ''}`}>
                    <td className={`p-2 font-bold text-sm text-center sticky left-0 z-10 ${ri === currentRound ? 'bg-blue-800 text-cyan-200' : 'bg-blue-900/80 text-cyan-400'}`}>
                      {ri === currentRound ? '▶ ' : ''}{ri + 1}
                    </td>
                    {players.slice(0, playerCount).map((_, pi) => {
                      const score = parseInt(scores[pi]?.[ri]);
                      const completed = !isNaN(score) && score < 50;
                      const isDealer = roundDealer === pi;
                      return (
                        <td key={pi} className={`p-1 ${isDealer && ri === currentRound ? 'bg-amber-500/10' : ''}`}>
                          <input
                            type="number"
                            inputMode="numeric"
                            value={scores[pi]?.[ri] || ''}
                            onChange={(e) => updateScore(pi, ri, e.target.value)}
                            className={`w-full text-center rounded-lg p-2 text-base focus:outline-none focus:ring-2 focus:ring-cyan-400 ${
                              completed ? 'bg-green-700/50 text-green-200' : 'bg-blue-800/50 text-white'
                            }`}
                            placeholder="–"
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              <tr className="bg-blue-950 font-bold border-t-2 border-blue-600">
                <td className="p-3 text-cyan-400 text-sm sticky left-0 bg-blue-950 z-10 w-12">TOT</td>
                {players.slice(0, playerCount).map((_, pi) => {
                  const rank = leaderboard.findIndex(p => p.index === pi);
                  return (
                    <td key={pi} className={`p-3 text-center text-lg ${
                      winner === pi ? 'text-yellow-300' :
                      rank === 0 && leaderboard.length > 0 ? 'text-cyan-300' : 'text-white'
                    }`}>
                      {getTotal(pi)}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Add/Remove Round Buttons */}
        <div className="flex justify-center gap-3 mt-3">
          <button
            onClick={addRound}
            className="bg-blue-700 hover:bg-blue-600 text-cyan-300 px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <span className="text-lg">+</span> Add Round
          </button>
          {rounds > 10 && (
            <button
              onClick={removeRound}
              className="bg-blue-800 hover:bg-blue-700 text-blue-300 px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              <span className="text-lg">−</span> Remove
            </button>
          )}
        </div>

        {/* Phase Reference */}
        <div className="mt-4 bg-blue-800/30 rounded-xl p-4 border border-blue-700/30">
          <h3 className="text-cyan-300 text-sm font-bold mb-3 text-center">📋 PHASE REQUIREMENTS</h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {phaseDescriptions.map((desc, i) => (
              <div key={i} className={`flex gap-2 p-1.5 rounded ${currentPhases.some(p => p === i + 1) ? 'bg-cyan-500/10 text-cyan-200' : 'text-blue-300'}`}>
                <span className="text-cyan-400 font-bold w-5">{i + 1}.</span>
                <span>{desc}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-center mt-4">
          <button onClick={resetGame} className="bg-red-600 hover:bg-red-500 text-white px-6 py-2 rounded-lg font-medium transition-colors">
            New Game
          </button>
        </div>

        <p className="text-center text-blue-400 text-xs mt-3">
          1-9 = 5pts • 10-12 = 10pts • Skip = 15pts • Wild = 25pts
          <br />
          <span className="text-green-400">Score &lt;50 = phase complete (green)</span>
        </p>
      </div>
    </div>
  );
}

export default Phase10Scorecard;
